import React from 'react';
import get from 'lodash/get';
import { Controller, useFieldArray, UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { Cell, HtmlTable } from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Input } from '../../../../../../../../CommonComponents/OPL/Input/Input';
import { IconButton } from '../../../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Button } from '../../../../../../../../CommonComponents/OPL/Button/Button';
import { formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { Label } from '../../../../../../../../CommonComponents/OPL/Label/Label';
import { SingleDatePicker } from '../../../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { BaseFormValues } from './EditAssetForm.meta';
import { useBeneficiaries } from '../../useBeneficiaries';
import { OptionType } from '../../../../../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { HelperText } from '../../../../../../../../CommonComponents/OPL/HelperText/HelperText';

interface Props {
  beneficiariesOptions?: OptionType<string>[];
  isDisabled?: boolean;
}

export const EditAssetForm = ({
  beneficiariesOptions: beneficiariesOptionsProp,
  isDisabled,
}: Props) => {
  const { data } = useBeneficiaries();

  const {
    control,
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<BaseFormValues>();

  if (!data) {
    return null;
  }

  const { beneficiariesOptions: baseBeneficiariesOptions, accountOwner } = data;

  const beneficiariesOptions = beneficiariesOptionsProp || baseBeneficiariesOptions;

  const primaryBeneficiariesActions = useFieldArray({
    control,
    name: 'PrimaryBeneficiaries',
  });

  const contingentBeneficiariesActions = useFieldArray({
    control,
    name: 'ContingentBeneficiaries',
  });

  const primaryBeneficiaries = watch('PrimaryBeneficiaries');
  const contingentBeneficiaries = watch('ContingentBeneficiaries');

  const totals = {
    PrimaryBeneficiaries: primaryBeneficiaries.reduce(
      (total, b) => total + parseFloat(b.Percentage.toString() || '0'),
      0,
    ),
    ContingentBeneficiaries: contingentBeneficiaries.reduce(
      (total, b) => total + parseFloat(b.Percentage.toString() || '0'),
      0,
    ),
  };

  const beneficiariesMap = [
    {
      name: 'Primary',
      fieldsKey: 'PrimaryBeneficiaries',
      fieldActions: primaryBeneficiariesActions,
    },
    {
      name: 'Contingent',
      fieldsKey: 'ContingentBeneficiaries',
      fieldActions: contingentBeneficiariesActions,
    },
    {
      name: 'Transfer Method',
      isSingleField: true,
    },
  ] as {
    name: 'Primary' | 'Contingent' | 'Transfer Method';
    isSingleField?: boolean;
    fieldsKey: 'PrimaryBeneficiaries' | 'ContingentBeneficiaries';
    fieldActions: UseFieldArrayReturn<
      BaseFormValues,
      'PrimaryBeneficiaries' | 'ContingentBeneficiaries',
      'id'
    >;
  }[];

  const filteredBeneficiariesOptions = beneficiariesOptions.reduce(
    (acc: OptionType<string>[], option) => {
      const hasItem = primaryBeneficiaries.find(primary => {
        if (primary.Id === null && accountOwner !== null) {
          return accountOwner.value === option.value;
        }
        return primary.Id === option.value;
      });

      if (hasItem) {
        return acc;
      }

      return [...acc, option];
    },
    [],
  );

  return (
    <>
      {beneficiariesMap.map(({ name, fieldsKey, fieldActions, isSingleField }) => (
        <HtmlTable mt="24" variant="embedded" key={name}>
          <table>
            <colgroup>
              <col width="60%" />
              <col width="40%" />
              <col width="1px" />
            </colgroup>

            <thead>
              <tr>
                <th>
                  <Text variant="smallCaps" textAlign="left">
                    {name}
                  </Text>
                </th>
                {!isSingleField && (
                  <th>
                    <Text variant="smallCaps" textAlign="left">
                      Allocation
                    </Text>
                  </th>
                )}

                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {isSingleField ? (
                <tr>
                  <Cell colSpan={2}>
                    <Controller
                      control={control}
                      name="TransferMethod"
                      render={({ field: { value, onChange } }) => {
                        const selectedOption =
                          typeof value === 'number'
                            ? data.transferMethodOptions.find(opt => opt.value === value)
                            : value;

                        return (
                          <Select
                            isPortal
                            label="Choose the way this property will transfer to heirs"
                            options={data.transferMethodOptions}
                            value={selectedOption}
                            onChange={opt => onChange(opt?.value)}
                            disabled={isDisabled}
                          />
                        );
                      }}
                    />
                  </Cell>
                </tr>
              ) : (
                <>
                  {' '}
                  {fieldActions.fields.map((beneficiary, index) => (
                    <tr key={beneficiary.id}>
                      <Cell css="vertical-align: top;">
                        <Controller
                          control={control}
                          name={`${fieldsKey}.${index}.Id`}
                          render={({ field: { value } }) => (
                            <Select
                              isPortal
                              isCreatable
                              formatCreateLabel={createValue => `Add ${createValue}`}
                              noOptionsMessage={() => 'Please type a beneficiary'}
                              value={beneficiariesOptions.find(opt => {
                                if (value === null && accountOwner !== null) {
                                  return opt.value === accountOwner.value;
                                }
                                return opt.value === value;
                              })}
                              options={filteredBeneficiariesOptions}
                              onChange={opt => {
                                if (opt) {
                                  const isNew = get(opt, '__isNew__', false);
                                  const id = isNew ? null : opt.value;

                                  setValue(`${fieldsKey}.${index}.Id`, id);
                                  setValue(`${fieldsKey}.${index}.Name`, opt.label);
                                }
                              }}
                              formVariant={errors[fieldsKey]?.[index]?.Name ? 'error' : undefined}
                              helperText={errors[fieldsKey]?.[index]?.Name?.message}
                              disabled={isDisabled}
                            />
                          )}
                        />
                      </Cell>
                      <Cell css="vertical-align: top;">
                        <Input
                          {...register(`${fieldsKey}.${index}.Percentage`)}
                          type="number"
                          formVariant={errors[fieldsKey]?.[index]?.Percentage ? 'error' : undefined}
                          helperText={errors[fieldsKey]?.[index]?.Percentage?.message}
                          innerText="%"
                          disabled={isDisabled}
                        />
                      </Cell>
                      <Cell css="vertical-align: top;">
                        <IconButton
                          variant="subtleInactive"
                          icon="far fa-minus-circle"
                          onClick={() => fieldActions.remove(index)}
                          disabled={isDisabled}
                        />
                      </Cell>
                    </tr>
                  ))}
                  <tr>
                    <Cell colSpan={3}>
                      <Button
                        variant="subtleActive"
                        leftIcon="fas fa-plus"
                        onClick={() =>
                          fieldActions.append({
                            Percentage: 100,
                          })
                        }
                        disabled={isDisabled}
                      >
                        Add Beneficiary
                      </Button>
                    </Cell>
                  </tr>
                  <tr>
                    <Cell>
                      <Text fontWeight={600} textAlign="right">
                        Total Allocation
                      </Text>
                    </Cell>
                    <Cell colSpan={2}>
                      <Box display="flex" alignItems="center" css="gap: 8px">
                        <Text fontWeight={600}>
                          {totals[fieldsKey] > 100
                            ? '+100%'
                            : formatPercentage(totals[fieldsKey] / 100)}
                        </Text>

                        {(name === 'Primary' && primaryBeneficiaries.length > 0) ||
                        (name === 'Contingent' && contingentBeneficiaries.length > 0)
                          ? totals[fieldsKey] !== 100 && (
                              <HelperText variant="error">The total must be 100%</HelperText>
                            )
                          : null}
                      </Box>
                    </Cell>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </HtmlTable>
      ))}

      <Box px="24">
        <Label mt="16" mb="4" css="display: block;">
          When were these instructions last updated?
        </Label>

        <Controller
          control={control}
          name="LastUpdated"
          render={({ field: { value, onChange } }) => {
            const dateValue = value ? new Date(value) : undefined;

            return (
              <SingleDatePicker
                disabled={isDisabled}
                date={dateValue ?? undefined}
                onChange={onChange}
                maxDate={new Date()}
                minDate={new Date(1900, 0)}
              />
            );
          }}
        />
      </Box>
    </>
  );
};
